import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Kill-switch admins - both accounts belong to Kai Jun (testing different
// roles), the sole dev on this system. Server-side check in server.js's
// POST /api/maintenance-status is the real enforcement; this list here is
// only used to decide whether to *show* the toggle UI and to bypass the
// maintenance screen when browsing as one of these accounts.
export const MAINTENANCE_ADMINS = ['ckj54313@gmail.com', 'kaijun.chong@ninjavan.co'];

export function MaintenanceScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', padding: 20, fontFamily: 'system-ui,-apple-system,sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '40px 32px', maxWidth: 420, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔧</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Under Maintenance</div>
        <div style={{ fontSize: 14, color: '#666', lineHeight: 1.6 }}>
          This part of the system is temporarily unavailable while we make improvements. Please check back shortly.
        </div>
      </div>
    </div>
  );
}

// area: 'sales' | 'approver'
// Returns { loading, blocked }. Render <MaintenanceScreen /> instead of the
// page's normal content when blocked is true.
//
// Also enforces the "force relogin after maintenance ends" requirement: if
// this browser previously saw maintenance ON for this area and the backend
// now reports it OFF with a newer updated_at than last seen, that means the
// toggle-off happened since we last checked - clear the saved login and
// hard-reload, so the user is forced through Google login again and picks
// up whatever new frontend bundle was just deployed rather than continuing
// to run old code that happened to still work after maintenance ended.
export function useMaintenanceCheck(area) {
  const { user } = useAuth();
  const [status, setStatus] = useState({ loading: true, blocked: false });

  useEffect(() => {
    fetch('/api/maintenance-status')
      .then(r => r.json())
      .then(data => {
        const isAdmin   = MAINTENANCE_ADMINS.includes((user?.email || '').toLowerCase());
        const flagged   = area === 'sales' ? data.sales : data.approver;
        const updatedAt = area === 'sales' ? data.salesUpdatedAt : data.approverUpdatedAt;
        const seenKey   = `maintenance_seen_${area}`;
        const lastSeen  = localStorage.getItem(seenKey);

        if (!flagged && updatedAt && lastSeen && lastSeen !== updatedAt) {
          localStorage.removeItem('ninja_credit_user');
          localStorage.setItem(seenKey, updatedAt);
          window.location.reload();
          return;
        }
        if (updatedAt) localStorage.setItem(seenKey, updatedAt);

        setStatus({ loading: false, blocked: !!flagged && !isAdmin });
      })
      .catch(() => setStatus({ loading: false, blocked: false }));
  }, [area, user?.email]);

  return status;
}

// Modal with two toggle switches (Sales / Approver), the submission
// blackout window (start/end time, MYT), and the two daily submission caps.
// Rendered from each sidebar's own "Maintenance Mode" item, visible only to
// accounts in MAINTENANCE_ADMINS. Toggles save instantly on click; the
// blackout/cap fields are free text/number inputs, so they use their own
// explicit Save button instead of saving on every keystroke.
export function MaintenanceTogglePanel({ onClose }) {
  const { user } = useAuth();
  const [status, setStatus] = useState({ sales: false, approver: false });
  const [settings, setSettings] = useState({ blackoutStart: '', blackoutEnd: '', dailyCapRequestor: '', dailyCapRegion: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    fetch('/api/maintenance-status')
      .then(r => r.json())
      .then(d => {
        setStatus({ sales: d.sales, approver: d.approver });
        setSettings({
          blackoutStart:     d.blackoutStart     || '',
          blackoutEnd:       d.blackoutEnd       || '',
          dailyCapRequestor: d.dailyCapRequestor || '',
          dailyCapRegion:    d.dailyCapRegion    || '',
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function toggle(area) {
    setSaving(true);
    const enabled = !status[area];
    try {
      await fetch('/api/maintenance-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ area, enabled, callerEmail: user.email }),
      });
      setStatus(s => ({ ...s, [area]: enabled }));
    } catch (e) { /* ignore - toggle just won't visually update */ }
    setSaving(false);
  }

  async function saveSettings() {
    setSaving(true);
    setSavedMsg('');
    try {
      await fetch('/api/maintenance-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settings: {
            blackout_start:      settings.blackoutStart,
            blackout_end:        settings.blackoutEnd,
            daily_cap_requestor: settings.dailyCapRequestor,
            daily_cap_region:    settings.dailyCapRegion,
          },
          callerEmail: user.email,
        }),
      });
      setSavedMsg('Saved');
      setTimeout(() => setSavedMsg(''), 2000);
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  const rows = [
    ['sales', 'Sales-facing (CAS, Dashboard, AR Summary, Monthly Review)'],
    ['approver', 'Approver-facing (Queue, Assessment, KYC, AR Portfolio)'],
  ];

  const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const sublabelStyle = { fontSize: 11, color: '#888', display: 'block', marginBottom: 4 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '24px 20px', maxWidth: 380, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>Maintenance Mode</div>
        {loading ? <div style={{ fontSize: 13, color: '#888' }}>Loading...</div> : (
          <>
            {rows.map(([area, label]) => (
              <div key={area} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0', gap: 12 }}>
                <div style={{ fontSize: 13, color: '#333', flex: 1 }}>{label}</div>
                <button onClick={() => toggle(area)} disabled={saving}
                  style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: saving ? 'default' : 'pointer', background: status[area] ? '#c5221f' : '#ccc', position: 'relative', flexShrink: 0, padding: 0 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: status[area] ? 23 : 3, transition: 'left 0.15s' }} />
                </button>
              </div>
            ))}

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Submission Blackout Window (MYT)</div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={sublabelStyle}>Start</label>
                  <input type="time" value={settings.blackoutStart} onChange={e => setSettings(s => ({ ...s, blackoutStart: e.target.value }))} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={sublabelStyle}>End</label>
                  <input type="time" value={settings.blackoutEnd} onChange={e => setSettings(s => ({ ...s, blackoutEnd: e.target.value }))} style={inputStyle} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginBottom: 16 }}>Leave both blank to disable. Shows the maintenance screen on the sales side during this window - can wrap past midnight (e.g. 22:00-06:00).</div>

              <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Daily Submission Caps</div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <label style={sublabelStyle}>Per Requestor</label>
                  <input type="number" min="0" placeholder="No limit" value={settings.dailyCapRequestor} onChange={e => setSettings(s => ({ ...s, dailyCapRequestor: e.target.value }))} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={sublabelStyle}>Per Region</label>
                  <input type="number" min="0" placeholder="No limit" value={settings.dailyCapRegion} onChange={e => setSettings(s => ({ ...s, dailyCapRegion: e.target.value }))} style={inputStyle} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginBottom: 14 }}>Blank means no limit.</div>

              <button onClick={saveSettings} disabled={saving}
                style={{ width: '100%', padding: '10px 0', background: '#c8102e', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {saving ? 'Saving...' : savedMsg || 'Save Blackout/Cap Settings'}
              </button>
            </div>
          </>
        )}
        <button style={{ marginTop: 16, width: '100%', padding: '10px 0', background: '#eee', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
